import {
  ArrowRightIcon,
  ArrowsDownUpIcon,
  ArrowsLeftRightIcon,
  CaretDownIcon,
  SlidersHorizontalIcon,
} from "@phosphor-icons/react";
import { times } from "lodash-es";
import { useMemo, useCallback } from "react";
import { defineMessages, useIntl } from "react-intl";

import {
  FilterButton,
  FilterButtonSkeleton,
} from "@/components/FilterButton/FilterButton";
import { HeaderSearchButton } from "@/components/HeaderSearchButton/HeaderSearchButton";
import { StickyHeader } from "@/components/StickyHeader/StickyHeader";
import { FlightsListDrawerComponent } from "@/features/flights/components/flight-list/types";
import { useFlightSearchDataContext } from "@/features/flights/contexts/FlightSearchDataContext";
import {
  TripType,
  type FlightFilters,
  type FlightsSortValue,
} from "@/features/flights/types/types";
import { SortOrder } from "@/types/enums";
import type { FlightFormLocation } from "@/types/types";
import { formatHeaderDate } from "@/utils/dates";

import { cabinClassLabels } from "../../utils/cabinClasses";
import { CalendarCards } from "../CalendarCards";

const sortOptionsLabels = defineMessages<SortOrder>({
  [SortOrder.ASC]: { id: "flights.cheapFirst" },
  [SortOrder.DESC]: { id: "flights.expensiveFirst" },
});

function formatLocation(location: FlightFormLocation) {
  return [
    location.code,
    "cityName" in location ? location.cityName : location.name,
  ]
    .filter(Boolean)
    .join(" ");
}

type Props = {
  selectedSortValue: FlightsSortValue;
  isLoading?: boolean;
  activeFilters: Record<keyof FlightFilters, boolean> | null;
  onFilterButtonClicked: (drawerComponent: FlightsListDrawerComponent) => void;
  onSearchDrawerOpen: () => void;
};

export function Header({
  selectedSortValue,
  isLoading,
  activeFilters,
  onSearchDrawerOpen,
  onFilterButtonClicked,
}: Props) {
  const intl = useIntl();
  const { searchData, updateSearchData } = useFlightSearchDataContext();

  const departureDate = searchData!.dates.departure;

  const passengerCount = useMemo(
    () => searchData!.adult + searchData!.child + searchData!.infantWithoutSeat,
    [searchData],
  );

  const handleDateChange = useCallback(
    (newDepartureDate: Date) => {
      updateSearchData((prevData) => {
        return {
          ...prevData!,
          dates: {
            ...prevData?.dates,
            departure: newDepartureDate,
          },
        };
      });
    },
    [updateSearchData],
  );

  return (
    <>
      <StickyHeader>
        <div className="flex items-center justify-between gap-2 py-4 text-text-primary">
          <div className="grow overflow-hidden">
            <HeaderSearchButton
              onClick={onSearchDrawerOpen}
              progress={isLoading ? { variant: "indeterminate" } : undefined}
            >
              <div className="flex w-full items-center gap-1.5 text-sm font-medium">
                <h3 className="truncate">{formatLocation(searchData!.from)}</h3>

                <span className="shrink-0">
                  {searchData!.tripType === TripType.ROUND_TRIP ? (
                    <ArrowsLeftRightIcon weight="bold" />
                  ) : (
                    <ArrowRightIcon weight="bold" />
                  )}
                </span>

                <h3 className="truncate">{formatLocation(searchData!.to)}</h3>
              </div>

              <div className="w-full truncate text-xs font-normal text-text-tertiary">
                {[
                  searchData!.tripType === TripType.ONE_WAY
                    ? formatHeaderDate(searchData!.dates.departure)
                    : `${formatHeaderDate(searchData!.dates.departure)} - ${formatHeaderDate(searchData!.dates.return!)}`,
                  intl.formatMessage(
                    { id: "numberOfTravellers" },
                    { count: passengerCount },
                  ),
                  intl.formatMessage(cabinClassLabels[searchData!.cabinClass]),
                ].map((item, index) => (
                  <span key={index}>
                    {index > 0 ? <> &bull; </> : null}
                    {item}
                  </span>
                ))}
              </div>
            </HeaderSearchButton>
          </div>
        </div>
        <div className="mb-2 flex gap-1 overflow-auto pt-px pb-2">
          {isLoading ? (
            times(4, (idx) => <FilterButtonSkeleton key={idx} />)
          ) : (
            <>
              <FilterButton
                onClick={() =>
                  onFilterButtonClicked(FlightsListDrawerComponent.SORT)
                }
              >
                {intl.formatMessage(sortOptionsLabels[selectedSortValue.order])}
                <ArrowsDownUpIcon size={20} className="fill-text-primary" />
              </FilterButton>
              <FilterButton
                onClick={() =>
                  onFilterButtonClicked(FlightsListDrawerComponent.ALL_FILTERS)
                }
                active={Object.values(activeFilters || {}).some(Boolean)}
              >
                {intl.formatMessage({ id: "flights.allFilters" })}
                <SlidersHorizontalIcon
                  size={20}
                  className="fill-text-primary"
                />
              </FilterButton>
              <FilterButton
                onClick={() =>
                  onFilterButtonClicked(FlightsListDrawerComponent.STOPS)
                }
                active={activeFilters?.stops || false}
              >
                {intl.formatMessage({ id: "flights.stops" })}
                <CaretDownIcon size={20} className="fill-text-primary" />
              </FilterButton>
              <FilterButton
                onClick={() =>
                  onFilterButtonClicked(FlightsListDrawerComponent.AIRLINES)
                }
                active={activeFilters?.selectedAirlines || false}
              >
                {intl.formatMessage({ id: "flights.airlines" })}
                <CaretDownIcon size={20} className="fill-text-primary" />
              </FilterButton>
            </>
          )}
        </div>
      </StickyHeader>

      <div className="flex shrink-0 flex-col gap-4 bg-surface-background">
        {searchData!.tripType === TripType.ONE_WAY && (
          <CalendarCards
            departureDate={departureDate}
            onDateChange={handleDateChange}
          />
        )}
      </div>
    </>
  );
}
